/**
 * The Create ad wizard's decisions, where a runner can reach them — the reason
 * `campaigns.ts` gives for being a module rather than a component.
 *
 * The wizard (`pages/CreateAd.tsx`, 2026-10-21) is one screen that sends the four
 * creates of `audits/2026-10-21/create-ad-workflow.md` §2 in order — campaign, line
 * item, one targeting criterion per country, promoted tweet — and this module holds
 * everything about that which is not rendering: what each step's draft must contain
 * before the next step opens, what X is sent, how each answer is read, and when the
 * run stops.
 *
 * 🔴 **The lock is not re-implemented here either.** `createOutcome` (campaigns.ts) and
 * `lineItemCreateOutcome` (line-items.ts) read the campaign's and the line item's
 * answers; this module maps their four kinds onto one step state and adds the two
 * answers they do not cover (a criterion, a promoted tweet). A run that meets anything
 * but `created` STOPS — it never retries, because `created: true` and
 * `created: "unknown"` both mean an object may be in X, and a second request makes a
 * second one (docs/API-CONTRACT.md; twads#86, #87).
 *
 * 🔴 **The Ads Manager number is `parseInt(id, 36)`, and that is an observation, not a
 * reading of X's documentation.** Three campaigns, three sources: `pergn` was read as
 * `42679175` in the owner's Ads Manager screenshot of 2026-10-08
 * (`audits/2026-10-08/x-ads-manager-observed.md:8`), and `ph9ff` was pasted by the
 * owner as `42795771` on 2026-10-19 (`audits/2026-10-19/account-check.txt:1`). Both
 * equal `parseInt(id, 36)`; `create-ad.test.ts` holds both pairs. It is shown for the
 * campaign and for nothing else: no line item's number has been seen on that screen.
 *
 * A third observation, 2026-10-21, and this one from X's API rather than from a
 * screen: X refused a line item under `phpvz` with *"Line items with different
 * objectives are not allowed under the same campaign (campaign_id=42817103)"*
 * (`audits/2026-10-21/attest-objective.txt:8–9`), and `42817103` is `parseInt("phpvz",
 * 36)`. The test holds that pair too.
 *
 * 🔴 **The ad step has two modes since PR B (2026-10-21): an existing tweet, or the
 * owner's own words and media.** The second adds two kinds of step before the promote —
 * one upload per file (`POST /api/v1/media`, polled through `GET /api/v1/media/:id`
 * while X reports processing) and one tweet (`POST /api/v1/tweets`) — and the tweet
 * is the object that outlives a stopped run: a nullcast tweet made and not promoted
 * stays in the account, so `createdSoFar` names it. Nothing is uploaded before Create
 * (paused): the files sit in the draft until the run reaches them.
 */
import type {
	ConversationSettings,
	CriterionType,
	CustomAudience,
	LineItemCreate,
	LineItemObjective,
	TargetingDevice,
	TargetingInterest,
	TargetingLanguage,
	TargetingLocation,
	TargetingNetworkOperator,
	TargetingPlatform,
	Tweet,
} from "./api.ts";
import { type CampaignCreate, campaignRequest, createOutcome } from "./campaigns.ts";
import {
	type DeliveryDraft,
	type DeliveryProblem,
	EMPTY_DELIVERY,
	deliveryBody,
	deliveryProblem,
} from "./line-item-delivery.ts";
import {
	type BidStrategy,
	type Placement,
	type PlacementProblem,
	bidNeedsAmount,
	placementsProblem,
	searchWithoutKeywords,
} from "./line-item-fields.ts";
import { lineItemCreateOutcome } from "./line-items.ts";
import { amountToMicro } from "./money.ts";
import { keywordLabel } from "./primary-targeting-copy.ts";
import { type KeywordType, PRIMARY_LIMITS } from "./primary-targeting.ts";
import { ageLabel, carrierLabel, genderLabel } from "./targeting-copy.ts";

/** The six steps, by index. The names are in `create-ad-copy.ts`. */
export type Step = 0 | 1 | 2 | 3 | 4 | 5;

/** The whole wizard as typed. Amounts and times are text until they are sent. */
export interface Draft {
	accountId: string;
	name: string;
	fundingInstrumentId: string;
	daily: string;
	total: string;
	/** `<input type="datetime-local">` values — local wall-clock, `YYYY-MM-DDTHH:mm`. */
	start: string;
	end: string;
	countries: TargetingLocation[];
	/** Countries to keep out (2026-10-29): each one a LOCATION criterion with NE, sent after `countries`. */
	excludedCountries: TargetingLocation[];
	/** No country on purpose: no country is targeted. With excluded countries beside it, whom X reaches is not in the reference. */
	everyone: boolean;
	/** X's languages, each one LANGUAGE criterion after the countries (2026-10-23). None: no language criterion. */
	languages: TargetingLanguage[];
	/** `"1"` or `"2"` — X's male and female — or `""` for no gender criterion, which X reads as all. */
	gender: string;
	/** One of the age buckets this console offers, or `""`: X allows one bucket per line item. */
	age: string;
	/** Ads Manager's Operating system: X's platforms, each one PLATFORM criterion. None: no criterion. */
	platforms: TargetingPlatform[];
	/** Ads Manager's Device model: X's devices, each one DEVICE criterion. None: no criterion. */
	devices: TargetingDevice[];
	/** Ads Manager's Carrier: X's carriers, each one NETWORK_OPERATOR criterion. None: no criterion. */
	carriers: TargetingNetworkOperator[];
	/**
	 * 🔴 PR 4, Ads Manager's Advanced targeting — the three that WIDEN the audience (X unions the
	 * «Primary» types, campaign-management.md:586–588). Each item is one criterion, sent after
	 * the carriers. X's interests, from the interests lookup.
	 */
	interests: TargetingInterest[];
	/** Keywords as typed, each with its match type: BROAD, PHRASE or EXACT. */
	keywords: { type: KeywordType; text: string }[];
	/** Follower look-alikes: X user ids, digits only, as text — no lookup from a handle exists. */
	lookalikes: string[];
	/** PR 6: the ad-group step's Delivery block, as typed; an empty field is not sent. */
	delivery: DeliveryDraft;
	/** Ads Manager's Custom audiences: the account's own, each one CUSTOM_AUDIENCE criterion. Only targetable ones are offered. */
	audiences: CustomAudience[];
	tweet: Tweet | null;
	/** One of the contract's two (`LineItemObjective`); the wizard starts on ENGAGEMENTS. */
	objective: LineItemObjective;
	/** X's name for the line item — optional (the reference: «The name for the line item»); sent only when given. */
	lineItemName: string;
	/** The line item's own total budget, as typed — optional; sent only when given (2026-10-22). */
	lineItemTotal: string;
	/**
	 * Where the daily budget lives (2026-10-23). `ad-group` sends the campaign with
	 * budgetOptimization LINE_ITEM and each ad group with its own daily budget; `campaign`
	 * sends neither, as before.
	 */
	budgetOn: BudgetOn;
	/** The ad group's daily budget, as typed — required when `budgetOn` is `ad-group`. */
	lineItemDaily: string;
	/** The optimisation goal; `""` is «X's default for the objective», and is not sent (PR 5). */
	goal: string;
	/** The bid strategy; AUTO is the worker's default and is not sent. */
	bidStrategy: BidStrategy;
	/** The bid amount as typed — asked for, and sent, only under MAX and TARGET. */
	bidAmount: string;
	/** The placements; `["ALL_ON_TWITTER"]` is the worker's default and is not sent. */
	placements: Placement[];
	/** «Write the ad» (the default) or «Use an existing tweet». */
	adMode: AdMode;
	/** The owner's words. Its length is X's to refuse; the counter beside it only counts. */
	adText: string;
	/** The tweet's `name` (≤ 80 code points); empty means the first words of the text. */
	adName: string;
	/** The files to upload at Create (paused) — not before. */
	adFiles: File[];
	/**
	 * A website link, appended to the tweet's text. 🔴 Required under WEBSITE_CLICKS: on
	 * 2026-10-21 X refused to promote a text-and-image tweet under that objective —
	 * «INVALID: Tweet : 2112870999612178835 not appropriate for objective WEBSITE_CLICKS»
	 * (`audits/2026-10-21/attest-creatives.txt`, request id cd0846f2f270f6e2); the
	 * reference's own words are «Tweet must have either a website card or a website link».
	 * Optional under ENGAGEMENTS.
	 */
	adUrl: string;
	/**
	 * Under WEBSITE_CLICKS, how a written ad carries its website: a **card** (image, headline,
	 * URL) or a **link** at the end of the text — X's rule is one or the other, «Tweet must have
	 * either a website card or a website link (not both).» (`urlRequired`). The card is the
	 * default by the owner's word (2026-10-23, `website-card-spec.md`), not by the reference.
	 */
	adWebsiteAs: WebsiteAs;
	/** The card's headline — `DETAILS.title`; the reference gives it as `string`, with no length. */
	cardTitle: string;
	/** The chosen image's size in pixels, read in the browser for the card's limits; null until read. */
	cardImage: ImageSize | null;
	/**
	 * Who can reply to the tweet this run makes — one of X's three, or "" for none chosen, in
	 * which case nothing is sent and X's own default (EVERYONE) applies; the console does not
	 * fill it in (PR 8, 2026-10-24).
	 */
	adReplies: ConversationSettings | "";
}

export type BudgetOn = "ad-group" | "campaign";
export type WebsiteAs = "card" | "link";
export interface ImageSize {
	width: number;
	height: number;
}

/**
 * A new draft. 🔴 The budget starts on each ad group, and the reason is an observation of
 * Ads Manager, not the reference: Ads Manager refused a console-made campaign with «Ad
 * group 1: Daily budget is required» (2026-10-23), and its own Edit campaign shows no
 * budget field. The reference only makes a line item's daily budget «Only available when
 * budget_optimization is set to LINE_ITEM for the parent campaign».
 */
export function newDraft(accountId: string, now: Date): Draft {
	return {
		accountId,
		name: "",
		fundingInstrumentId: "",
		daily: "",
		total: "",
		...defaultSchedule(now),
		countries: [],
		excludedCountries: [],
		everyone: false,
		languages: [],
		gender: "",
		age: "",
		platforms: [],
		devices: [],
		carriers: [],
		audiences: [],
		tweet: null,
		objective: "ENGAGEMENTS",
		lineItemName: "",
		lineItemTotal: "",
		budgetOn: "ad-group",
		lineItemDaily: "",
		goal: "",
		bidStrategy: "AUTO",
		bidAmount: "",
		placements: ["ALL_ON_TWITTER"],
		interests: [],
		keywords: [],
		lookalikes: [],
		delivery: { ...EMPTY_DELIVERY },
		adMode: "write",
		adText: "",
		adName: "",
		adFiles: [],
		adUrl: "",
		adWebsiteAs: "card",
		cardTitle: "",
		cardImage: null,
		adReplies: "",
	};
}

/**
 * Whether the line item's objective needs a website in the tweet — a website card or a
 * website link. The guide's WEBSITE_CLICKS row: «Tweet must have either a website card or a
 * website link (not both).» (https://docs.x.com/x-ads-api/campaign-management, Objective
 * Based Campaigns; audits/2026-10-23/x-docs/campaign-management.md:386) — and X refused a
 * promote with neither on 2026-10-21 (`adUrl` above).
 */
export function urlRequired(objective: LineItemObjective): boolean {
	return objective === "WEBSITE_CLICKS";
}

/** Whether this written ad goes out as a website card rather than a link in its text. */
export function cardMode(d: Pick<Draft, "objective" | "adMode" | "adWebsiteAs">): boolean {
	return urlRequired(d.objective) && d.adMode === "write" && d.adWebsiteAs === "card";
}

/** An http(s) link anywhere in a text — the worker's own test for «a website link» beside a card. */
const TEXT_LINK = /https?:\/\//i;

/** An http(s) URL the browser can parse; anything else is not sent as a link. */
export function isWebUrl(text: string): boolean {
	try {
		const u = new URL(text.trim());
		return u.protocol === "https:" || u.protocol === "http:";
	} catch {
		return false;
	}
}

/** The tweet's text as sent: the words, then the link, or undefined when there is neither. */
export function tweetText(d: Pick<Draft, "adText" | "adUrl">): string | undefined {
	const parts = [d.adText.trim(), d.adUrl.trim()].filter((p) => p !== "");
	return parts.length === 0 ? undefined : parts.join(" ");
}

/**
 * 🔴 Under WEBSITE_CLICKS, a picked tweet whose text carries no http(s) at all — the one
 * thing about its link this console's read can tell. A warning, never a refusal: X's rule
 * is a card OR a link (`urlRequired`), and `GET /api/v1/tweets` carries the text and
 * neither `card_uri` nor `entities` (`tweetView` in the worker's routes/v1.ts), so a tweet
 * with a website card and no link in its text is right by X's rule and unseen here.
 *
 * The other direction is unseen too, and let through on purpose: a t.co in the text may
 * be the tweet's media, not a website. 2112870999612178835 read «… across EMEA.
 * https://t.co/gjpeWKz6hg», and X refused it under WEBSITE_CLICKS on 2026-10-21 («not
 * appropriate for objective WEBSITE_CLICKS», audits/2026-10-21/attest-creatives.txt:50).
 * X answers that at the promote.
 */
export function linkUnseen(
	objective: LineItemObjective,
	tweet: Pick<Tweet, "text"> | null,
): boolean {
	if (!urlRequired(objective) || tweet === null) return false;
	return !/https?:\/\//i.test(tweet.text ?? "");
}

export type AdMode = "write" | "existing";

/**
 * What the reference allows on one tweet — «up to 4 images, 1 animated GIF, or 1 video»
 * (POST …/tweet, `media_keys`, read 2026-10-07) — read as the two shapes this console
 * offers: up to four images, or one GIF. Video is not offered (the limits in the
 * reference disagree, and the Worker takes bodies to 100 MB; `own-creatives-spec.md`
 * rev. 3 §3). The sizes are the reference's for the simple upload, 5 MB and 15 MB
 * (`x-reference-creatives-verbatim.md` §2), read as 1024² bytes as the worker reads
 * them — a file between the two readings is sent, and X answers.
 */
export const AD_MEDIA_MAX = 4;
export const AD_NAME_MAX = 80;
const MIB = 1024 * 1024;
export const MEDIA_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const MEDIA_GIF_TYPE = "image/gif";
export const MEDIA_MAX_BYTES = { IMAGE: 5 * MIB, GIF: 15 * MIB } as const;

/**
 * 🔴 The image limits for a card — the creatives guide, «Cards» → «Image» (CG:40–48,
 * audits/2026-10-23/x-docs/creatives.md, read 2026-10-23): «Images must be 3MB or less and
 * have a width of at least 800px. … Website: 1:1 and 1.91:1 … We support the following image
 * formats: .bmp, .jpeg, and .png.» Stricter than the tweet's upload, so the step checks them
 * BEFORE the upload rather than leave the refusal to X after it.
 *
 * - Types: jpeg and png. The guide also names .bmp, and this console's upload does not take
 *   it (`MEDIA_IMAGE_TYPES`; the worker's `MEDIA_CONTENT_TYPES`) — named, not added here.
 * - «3MB» read as 1024² bytes, as `MEDIA_MAX_BYTES` reads its megabytes: the larger reading
 *   is sent, and what X answers to a file between the readings is not observed.
 * - The height is not checked: the guide names none — it follows from the ratio.
 * - 🔴 The ratio is a WARNING, never a refusal, and its tolerance is this console's, not
 *   X's: the guide says «1:1 and 1.91:1» and no tolerance, and the common 1200×628 is 1.9108.
 *   ±1% (f5, 2026-10-23) lets that through and flags what is plainly neither.
 */
export const CARD_IMAGE_TYPES = ["image/jpeg", "image/png"] as const;
export const CARD_IMAGE_MAX_BYTES = 3 * MIB;
export const CARD_IMAGE_MIN_WIDTH = 800;
export const CARD_RATIOS = [1, 1.91] as const;
export const CARD_RATIO_TOLERANCE = 0.01;

/** Why the card's image cannot be sent, or null — one image, its type, its size, its width. */
export function cardImageProblem(files: readonly File[], size: ImageSize | null): Problem | null {
	if (files.length !== 1) return "card-image-count";
	const file = files[0];
	if (!(CARD_IMAGE_TYPES as readonly string[]).includes(file.type)) return "card-image-type";
	if (file.size === 0 || file.size > CARD_IMAGE_MAX_BYTES) return "card-image-size";
	if (size === null) return "card-image-unread";
	if (size.width < CARD_IMAGE_MIN_WIDTH) return "card-image-width";
	return null;
}

/** Whether the image is outside ±1% of both of the guide's ratios — a warning, never a refusal. */
export function cardRatioOff(size: ImageSize | null): boolean {
	if (size === null || size.width <= 0 || size.height <= 0) return false;
	const ratio = size.width / size.height;
	return !CARD_RATIOS.some((r) => Math.abs(ratio - r) / r <= CARD_RATIO_TOLERANCE);
}

export type MediaKind = "IMAGE" | "GIF";

/** Which of the two kinds a file is, by its declared type — or neither. */
export function mediaKindOf(file: Pick<File, "type">): MediaKind | null {
	if (file.type === MEDIA_GIF_TYPE) return "GIF";
	return (MEDIA_IMAGE_TYPES as readonly string[]).includes(file.type) ? "IMAGE" : null;
}

/**
 * The default `name` for a tweet the owner did not name: the text's opening, cut at a
 * word boundary to at most 80 code points — code points, not UTF-16 units, so an emoji
 * counts once, as X counts characters.
 */
export function defaultTweetName(text: string): string {
	const points = [...text.trim().replace(/\s+/g, " ")];
	if (points.length <= AD_NAME_MAX) return points.join("");
	const cut = points.slice(0, AD_NAME_MAX).join("");
	const space = cut.lastIndexOf(" ");
	return (space > 0 ? cut.slice(0, space) : cut).trim();
}

/** The code-point length of a name, the unit X's «80 characters» is read in. */
export function nameLength(name: string): number {
	return [...name].length;
}

/**
 * Why a set of files cannot be sent, or null. In the order the reference states the
 * rule: the count, then the mix (a GIF goes alone), then each file's type and size.
 */
export function mediaProblem(files: readonly File[]): Problem | null {
	if (files.length > AD_MEDIA_MAX) return "media-count";
	const kinds = files.map(mediaKindOf);
	if (kinds.includes(null)) return "media-type";
	if (kinds.includes("GIF") && files.length > 1) return "media-gif-alone";
	for (const [i, f] of files.entries()) {
		const kind = kinds[i];
		if (kind !== null && f.size > MEDIA_MAX_BYTES[kind]) return "media-size";
		if (f.size === 0) return "media-size";
	}
	return null;
}

/** The country search waits this long after the last keystroke before asking X. */
export const SEARCH_DEBOUNCE_MS = 300;

const MINUTE_MS = 60_000;
const START_IN_MINUTES = 15;
const FLIGHT_DAYS = 7;

const two = (n: number) => String(n).padStart(2, "0");

/** A date as `<input type="datetime-local">` wants it: local time, to the minute. */
export function localInputValue(d: Date): string {
	return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}`;
}

/**
 * The prefilled flight: start fifteen minutes from now, end seven days after that —
 * seven calendar days at the same wall-clock time, so a clock change in between (the
 * one of 2026-10-25, for the run this screen was built for) does not move the end by
 * an hour.
 */
export function defaultSchedule(now: Date): { start: string; end: string } {
	const start = new Date(now.getTime() + START_IN_MINUTES * MINUTE_MS);
	const end = new Date(start);
	end.setDate(end.getDate() + FLIGHT_DAYS);
	return { start: localInputValue(start), end: localInputValue(end) };
}

/**
 * What X is sent for a local value: UTC, whole seconds, `Z` — the shape the worker
 * itself sends on (`xTimeToSecond`, platform/x-api.ts) and the shape of the reference
 * run (`2026-10-21T09:10:00Z`). `null` when the field holds no time, so the helper
 * under the field can say so instead of showing `Invalid Date`.
 */
export function utcIso(local: string): string | null {
	if (local.trim() === "") return null;
	const when = new Date(local);
	if (Number.isNaN(when.getTime())) return null;
	return `${when.toISOString().slice(0, 19)}Z`;
}

/** A budget field as typed: empty, an amount in micro units, or neither. */
export type BudgetField =
	| { kind: "absent" }
	| { kind: "amount"; micro: number }
	| { kind: "invalid" };

export function budgetField(text: string): BudgetField {
	if (text.trim() === "") return { kind: "absent" };
	const micro = amountToMicro(text);
	return micro === null ? { kind: "invalid" } : { kind: "amount", micro };
}

/** What stops a step from opening the next. Worded in `create-ad-copy.ts`. */
export type Problem =
	| "account"
	| "name"
	| "instrument"
	| "daily-missing"
	| "daily-invalid"
	| "total-invalid"
	| "ad-group-total-invalid"
	| "ad-group-daily-missing"
	| "ad-group-daily-invalid"
	| "bid-amount-missing"
	| "bid-amount-invalid"
	| PlacementProblem
	| "search-needs-keyword"
	| DeliveryProblem
	| "start"
	| "end"
	| "end-before-start"
	| "no-country"
	| "too-many-interests"
	| "too-many-keywords"
	| "too-many-lookalikes"
	| "no-tweet"
	| "ad-empty"
	| "ad-name"
	| "ad-url"
	| "ad-url-invalid"
	| "media-count"
	| "media-gif-alone"
	| "media-type"
	| "media-size"
	| "card-title"
	| "card-text-link"
	| "card-image-count"
	| "card-image-type"
	| "card-image-size"
	| "card-image-unread"
	| "card-image-width";

/**
 * The one check per step, in the order the fields stand on the screen. `null` means
 * the step may close. The audience step is the one with a deliberate way past it:
 * no country is refused UNLESS `everyone` is ticked, because by X's documentation no
 * criterion means all users worldwide, and that has to be chosen rather than
 * defaulted into (NO_CRITERIA in targeting-copy.ts).
 */
export function stepProblem(step: Step, d: Draft): Problem | null {
	switch (step) {
		case 0:
			return d.accountId ? null : "account";
		case 1: {
			if (!d.name.trim()) return "name";
			if (!d.fundingInstrumentId) return "instrument";
			// With the budget on each ad group the campaign has no daily budget: X refuses one
			// beside LINE_ITEM («Please remove your daily campaign budget before turning on
			// campaign budget optimization», request 6e24d969badff983, 2026-10-23).
			if (d.budgetOn === "campaign") {
				const daily = budgetField(d.daily);
				if (daily.kind === "absent") return "daily-missing";
				if (daily.kind === "invalid") return "daily-invalid";
			}
			if (budgetField(d.total).kind === "invalid") return "total-invalid";
			return null;
		}
		case 2: {
			if (d.budgetOn === "ad-group") {
				const daily = budgetField(d.lineItemDaily);
				if (daily.kind === "absent") return "ad-group-daily-missing";
				if (daily.kind === "invalid") return "ad-group-daily-invalid";
			}
			if (budgetField(d.lineItemTotal).kind === "invalid") return "ad-group-total-invalid";
			if (bidNeedsAmount(d.bidStrategy)) {
				const bid = budgetField(d.bidAmount);
				if (bid.kind === "absent") return "bid-amount-missing";
				// X: «Only values greater than zero are accepted» (R:3051).
				if (bid.kind === "invalid" || bid.micro <= 0) return "bid-amount-invalid";
			}
			const placements = placementsProblem(d.placements);
			if (placements !== null) return placements;
			const delivery = deliveryProblem(d.objective, d.delivery);
			if (delivery !== null) return delivery;
			const start = utcIso(d.start);
			const end = utcIso(d.end);
			if (start === null) return "start";
			if (end === null) return "end";
			return end > start ? null : "end-before-start";
		}
		case 3:
			if (d.countries.length === 0 && !d.everyone) return "no-country";
			// The guide's limits per line item (G:497, G:505, G:501): counted here, before X.
			if (d.interests.length > PRIMARY_LIMITS.interests) return "too-many-interests";
			if (d.keywords.length > PRIMARY_LIMITS.keywords) return "too-many-keywords";
			if (d.lookalikes.length > PRIMARY_LIMITS.lookalikes) return "too-many-lookalikes";
			// 🔴 G:883 — asked here because the keywords are this step's: the placements were
			// chosen on the ad-group step, before them, and the worker cannot see both in one body.
			return searchWithoutKeywords(d.placements, d.keywords.length) ? "search-needs-keyword" : null;
		case 4:
			return adProblem(d);
		case 5:
			return null;
	}
}

/**
 * The ad step's own problems — the wizard's step 4 and the card's Add ad ask the same
 * question of the same fields (M2, 2026-10-21). An existing tweet needs the pick; a
 * written ad needs a readable link, the link under WEBSITE_CLICKS, some text or a file,
 * a name within the reference's maximum, and files the reference allows.
 */
export function adProblem(
	d: Pick<
		Draft,
		| "adMode"
		| "tweet"
		| "adUrl"
		| "objective"
		| "adText"
		| "adFiles"
		| "adName"
		| "adWebsiteAs"
		| "cardTitle"
		| "cardImage"
	>,
): Problem | null {
	if (d.adMode === "existing") return d.tweet === null ? "no-tweet" : null;
	if (cardMode(d)) {
		// The card: its destination, its headline, the tweet's words — required, because the
		// tweet carries no media_keys (the image is the card's) and the reference makes text
		// «Required if no media_keys are specified» — with no link in them (not both), and
		// one image within the guide's limits.
		if (d.adUrl.trim() === "") return "ad-url";
		if (!isWebUrl(d.adUrl)) return "ad-url-invalid";
		if (d.cardTitle.trim() === "") return "card-title";
		if (d.adText.trim() === "") return "ad-empty";
		if (TEXT_LINK.test(d.adText)) return "card-text-link";
		if (nameLength(d.adName) > AD_NAME_MAX) return "ad-name";
		return cardImageProblem(d.adFiles, d.cardImage);
	}
	if (d.adUrl.trim() !== "" && !isWebUrl(d.adUrl)) return "ad-url-invalid";
	if (urlRequired(d.objective) && d.adUrl.trim() === "") return "ad-url";
	if (tweetText(d) === undefined && d.adFiles.length === 0) return "ad-empty";
	if (nameLength(d.adName) > AD_NAME_MAX) return "ad-name";
	return mediaProblem(d.adFiles);
}

/**
 * `POST /api/v1/campaigns`' body, through `campaignRequest` so the worker's own
 * order and words apply — the amounts arrive there as the micro strings that form
 * takes, converted here by `money.ts` and nowhere else.
 */
export function campaignCreate(
	account: string,
	d: Draft,
): { ok: true; value: CampaignCreate } | { ok: false; error: string } {
	const daily = budgetField(d.daily);
	const total = budgetField(d.total);
	return campaignRequest(account, {
		name: d.name,
		fundingInstrumentId: d.fundingInstrumentId,
		dailyBudgetAmountLocalMicro:
			d.budgetOn === "campaign" && daily.kind === "amount" ? String(daily.micro) : "",
		totalBudgetAmountLocalMicro: total.kind === "amount" ? String(total.micro) : "",
		...(d.budgetOn === "ad-group" && { budgetOptimization: "LINE_ITEM" as const }),
	});
}

/** `POST /api/v1/line-items`' body — the flight in UTC, under the campaign just made. */
export function lineItemCreate(
	account: string,
	campaignId: string,
	d: Draft,
): { ok: true; value: LineItemCreate } | { ok: false; problem: Problem } {
	const problem = stepProblem(2, d);
	if (problem !== null) return { ok: false, problem };
	// Both are strings here: stepProblem(2) returned null, so neither utcIso is null.
	const value: LineItemCreate = {
		account,
		campaignId,
		startTime: utcIso(d.start) ?? "",
		endTime: utcIso(d.end) ?? "",
		objective: d.objective,
	};
	// Sent only when given: the worker refuses a blank name, and an absent one is X's default.
	if (d.lineItemName.trim() !== "") value.name = d.lineItemName.trim();
	const total = budgetField(d.lineItemTotal);
	if (total.kind === "amount") value.totalBudgetAmountLocalMicro = total.micro;
	// Only with the budget on the ad group: under a campaign X did not make LINE_ITEM the
	// worker refuses it, and the field is not on the screen then.
	const daily = budgetField(d.lineItemDaily);
	if (d.budgetOn === "ad-group" && daily.kind === "amount")
		value.dailyBudgetAmountLocalMicro = daily.micro;
	// PR 5: each sent only when it is not the worker's default, so a draft left as it opens
	// sends the body of yesterday.
	if (!(d.placements.length === 1 && d.placements[0] === "ALL_ON_TWITTER"))
		value.placements = [...d.placements];
	if (d.bidStrategy !== "AUTO") {
		value.bidStrategy = d.bidStrategy;
		const bid = budgetField(d.bidAmount);
		if (bid.kind === "amount") value.bidAmountLocalMicro = bid.micro;
	}
	if (d.goal !== "") value.goal = d.goal;
	// PR 6: only the fields given. Pacing only with the budget on each ad group: X makes it
	// «Only available when budget_optimization is set to LINE_ITEM for the parent campaign»
	// (R:3170), and with the budget on the campaign the parent is not LINE_ITEM.
	const { standardDelivery, ...delivery } = deliveryBody(d.objective, d.delivery);
	Object.assign(value, delivery);
	if (standardDelivery !== undefined && d.budgetOn === "ad-group")
		value.standardDelivery = standardDelivery;
	return { ok: true, value };
}

/**
 * The campaign budgets the bid is above — `[]` when none, or under AUTO. The guide: the
 * bid «should not exceed daily_budget_amount_local_micro or total_budget_amount_local_micro
 * of the parent campaign» (G:911). «Should»: the wizard warns and still sends (lead f5,
 * 2026-10-23), as it does for an ad group's total above the campaign's.
 */
export function bidOverCampaign(d: Draft): ("daily" | "total")[] {
	if (!bidNeedsAmount(d.bidStrategy)) return [];
	const bid = budgetField(d.bidAmount);
	if (bid.kind !== "amount") return [];
	const over: ("daily" | "total")[] = [];
	// The campaign's daily budget only when it has one: with the budget on each ad group the
	// campaign is sent without it (X refuses the pair, #195), whatever the field still holds.
	for (const [key, text] of [
		["daily", d.budgetOn === "campaign" ? d.daily : ""],
		["total", d.total],
	] as const) {
		const budget = budgetField(text);
		if (budget.kind === "amount" && bid.micro > budget.micro) over.push(key);
	}
	return over;
}

/**
 * One step of the run, as the screen shows it.
 *
 *   pending   not sent — the run has not reached it, or stopped before it
 *   running   sent, waiting for X
 *   done      X answered 201 and named the object — or, for a criterion, answered
 *             `created: true` with an id on another status; `note` then carries that
 *             status, so the line does not read as a clean 201
 *   stopped   anything else; `why` says which kind, `id` is the object when the
 *             answer named one, and `mayExist` is whether a retry could make a second
 */
export type StepState =
	| { kind: "pending" }
	| { kind: "running" }
	| {
			kind: "done";
			id: string;
			note?: string;
			/** The upload's `processing.state` as X sent it, or null — the wizard polls while it is not succeeded. */
			processing?: string | null;
			/** The promoted tweet's `approvalStatus` as X sent it, for the ad step. */
			approvalStatus?: string | null;
			/** The card's `cardUri` as the card's read-back answered it — what the tweet is made with. */
			cardUri?: string;
			/**
			 * A criterion that answered `created: true` on another status: it exists and its
			 * read-back did not come — so the run goes on, and is not finished (`finishedRun`).
			 */
			readBack?: false;
	  }
	| {
			kind: "stopped";
			why: "exists" | "unknown" | "refused";
			id: string | null;
			error: string;
			mayExist: boolean;
	  };

/** One criterion the run sends: its type, its value, and a name for the row. */
export interface SentCriterion {
	name: string;
	targetingType: CriterionType;
	targetingValue: string;
	/** Present for an excluded country alone; every other criterion goes without one, as EQ. */
	operatorType?: "NE";
}

/**
 * Everything the audience step chose, in the order the run sends it: the countries,
 * then the countries excluded (NE, since 2026-10-29), then the languages, then the gender, then the age bucket, then the operating
 * systems, devices and carriers; then the types X unions — interests, keywords,
 * look-alikes, custom audiences — one criterion each. Any field after the age bucket
 * may be absent from `d` (a draft from before it), and absent sends nothing.
 */
export function criteriaToSend(
	d: Pick<Draft, "countries" | "languages" | "gender" | "age"> &
		Partial<
			Pick<
				Draft,
				| "excludedCountries"
				| "platforms"
				| "devices"
				| "carriers"
				| "interests"
				| "keywords"
				| "lookalikes"
				| "audiences"
			>
		>,
): SentCriterion[] {
	return [
		...d.countries.map((c) => ({
			name: c.name,
			targetingType: "LOCATION" as const,
			targetingValue: c.targetingValue,
		})),
		...(d.excludedCountries ?? []).map((c) => ({
			name: `not ${c.name}`,
			targetingType: "LOCATION" as const,
			targetingValue: c.targetingValue,
			operatorType: "NE" as const,
		})),
		...d.languages.map((l) => ({
			name: l.name,
			targetingType: "LANGUAGE" as const,
			targetingValue: l.targetingValue,
		})),
		...(d.gender === ""
			? []
			: [
					{
						name: genderLabel(d.gender),
						targetingType: "GENDER" as const,
						targetingValue: d.gender,
					},
				]),
		...(d.age === ""
			? []
			: [{ name: `age ${ageLabel(d.age)}`, targetingType: "AGE" as const, targetingValue: d.age }]),
		...(d.platforms ?? []).map((p) => ({
			name: p.name,
			targetingType: "PLATFORM" as const,
			targetingValue: p.targetingValue,
		})),
		...(d.devices ?? []).map((v) => ({
			name: v.name,
			targetingType: "DEVICE" as const,
			targetingValue: v.targetingValue,
		})),
		...(d.carriers ?? []).map((o) => ({
			name: carrierLabel(o),
			targetingType: "NETWORK_OPERATOR" as const,
			targetingValue: o.targetingValue,
		})),
		// PR 4: the primary types last — each one widens what the ones before it narrowed.
		...(d.interests ?? []).map((i) => ({
			name: `interest ${i.name}`,
			targetingType: "INTEREST" as const,
			targetingValue: i.targetingValue,
		})),
		...(d.keywords ?? []).map((k) => ({
			name: keywordLabel(k),
			targetingType: k.type,
			targetingValue: k.text,
		})),
		...(d.lookalikes ?? []).map((id) => ({
			name: `like the followers of user ${id}`,
			targetingType: "SIMILAR_TO_FOLLOWERS_OF_USER" as const,
			targetingValue: id,
		})),
		...(d.audiences ?? []).map((a) => ({
			name: a.name,
			targetingType: "CUSTOM_AUDIENCE" as const,
			targetingValue: a.id,
		})),
	];
}

const CRITERION_TYPES: readonly CriterionType[] = [
	"LOCATION",
	"LANGUAGE",
	"GENDER",
	"AGE",
	"PLATFORM",
	"DEVICE",
	"NETWORK_OPERATOR",
	"INTEREST",
	"BROAD_KEYWORD",
	"PHRASE_KEYWORD",
	"EXACT_KEYWORD",
	"SIMILAR_TO_FOLLOWERS_OF_USER",
	"CUSTOM_AUDIENCE",
];

export interface Run {
	campaign: StepState;
	lineItem: StepState;
	/** One per criterion, in the order sent (`criteriaToSend`); `name` is for the row. */
	targeting: {
		name: string;
		targetingType: CriterionType;
		targetingValue: string;
		/** An excluded country's NE; absent is EQ. */
		operatorType?: "NE";
		state: StepState;
	}[];
	/** One per file, in the order chosen; empty when an existing tweet is used. A done step's `id` is the media key. */
	media: { name: string; state: StepState }[];
	/** The website card this run makes, or null when the ad carries none. */
	card: StepState | null;
	/** The tweet this run makes, or null when an existing tweet is promoted. */
	tweet: StepState | null;
	ad: StepState;
}

export function freshRun(
	/** `criteriaToSend`'s rows; a row with no type is a place, as X's locations name theirs. */
	criteria: readonly {
		name: string;
		targetingValue: string;
		targetingType: string | null;
		operatorType?: "NE";
	}[],
	files: readonly Pick<File, "name">[] = [],
	mode: AdMode = "existing",
	withCard = false,
): Run {
	return {
		campaign: { kind: "pending" },
		lineItem: { kind: "pending" },
		targeting: criteria.map((c) => ({
			name: c.name,
			targetingType: CRITERION_TYPES.includes(c.targetingType as CriterionType)
				? (c.targetingType as CriterionType)
				: "LOCATION",
			targetingValue: c.targetingValue,
			...(c.operatorType === "NE" ? { operatorType: "NE" as const } : {}),
			state: { kind: "pending" },
		})),
		media: mode === "write" ? files.map((f) => ({ name: f.name, state: { kind: "pending" } })) : [],
		card: mode === "write" && withCard ? { kind: "pending" } : null,
		tweet: mode === "write" ? { kind: "pending" } : null,
		ad: { kind: "pending" },
	};
}

const record = (body: unknown): Record<string, unknown> =>
	typeof body === "object" && body !== null && !Array.isArray(body)
		? (body as Record<string, unknown>)
		: {};

const errorOf = (b: Record<string, unknown>, status: number): string =>
	typeof b.error === "string" ? b.error : `HTTP ${status}`;

/** The campaign's answer, read by the campaign lock and then put on a step. */
export function campaignStep(status: number, body: unknown): StepState {
	const o = createOutcome(status, body);
	switch (o.kind) {
		case "created":
			return { kind: "done", id: o.id };
		case "exists":
			return { kind: "stopped", why: "exists", id: o.id, error: o.error, mayExist: true };
		case "unknown":
			return { kind: "stopped", why: "unknown", id: null, error: o.error, mayExist: true };
		case "refused":
			return { kind: "stopped", why: "refused", id: null, error: o.error, mayExist: false };
	}
}

/** The line item's answer, read by the same lock through `lineItemCreateOutcome`. */
export function lineItemStep(status: number, body: unknown): StepState {
	const o = lineItemCreateOutcome(status, body);
	switch (o.kind) {
		case "created":
			return { kind: "done", id: o.id };
		case "exists":
			return { kind: "stopped", why: "exists", id: o.id, error: o.error, mayExist: true };
		case "unknown":
			return { kind: "stopped", why: "unknown", id: null, error: o.error, mayExist: true };
		case "refused":
			return { kind: "stopped", why: "refused", id: null, error: o.error, mayExist: false };
	}
}

/**
 * A criterion's answer, in the targeting panel's own reading (`Targeting.tsx`, `add`):
 * `created: true` is the narrowing existing — a duplicate place narrows to the same
 * place — so with an id the run goes on; `created: "unknown"` is a narrowing that may
 * not have happened, and the run stops rather than start a line item that may reach
 * everyone (CRITERION_UNKNOWN in targeting-copy.ts).
 */
export function criterionStep(status: number, body: unknown): StepState {
	const b = record(body);
	if (b.created === true && typeof b.id === "string")
		return status === 201
			? { kind: "done", id: b.id }
			: {
					kind: "done",
					id: b.id,
					readBack: false,
					note: `X answered ${status} with created: true — the criterion exists; its read-back did not: ${errorOf(b, status)}`,
				};
	if (b.created === true)
		return { kind: "stopped", why: "exists", id: null, error: errorOf(b, status), mayExist: true };
	if (b.created === "unknown")
		return { kind: "stopped", why: "unknown", id: null, error: errorOf(b, status), mayExist: true };
	if (status === 201 && typeof b.id === "string") return { kind: "done", id: b.id };
	return { kind: "stopped", why: "refused", id: null, error: errorOf(b, status), mayExist: false };
}

/**
 * The promoted tweet's answer. A 201 carries `ids`, one per tweet sent — one here —
 * and a 502 can carry `created` (the creative panel's reading, `Creative.tsx`,
 * `promote`). A 409 is the console's own refusal of a parent that is not PAUSED; the
 * line item was created PAUSED a moment ago, so meeting it means somebody switched it
 * on in between, and the run stops with X's sentence.
 */
export function adStep(status: number, body: unknown): StepState {
	const b = record(body);
	const ids = Array.isArray(b.ids) ? b.ids.filter((x): x is string => typeof x === "string") : [];
	if (status === 201 && ids.length > 0) {
		const rows = Array.isArray(b.promotedTweets) ? b.promotedTweets : [];
		const first = record(rows[0]);
		return {
			kind: "done",
			id: ids[0],
			// X's review of the creative, as the attach answered it — `creative-copy.ts`
			// words it; null when the row carries none.
			approvalStatus: typeof first.approvalStatus === "string" ? first.approvalStatus : null,
		};
	}
	if (b.created === true)
		return {
			kind: "stopped",
			why: "exists",
			id: ids[0] ?? null,
			error: errorOf(b, status),
			mayExist: true,
		};
	if (b.created === "unknown" || status === 201)
		return { kind: "stopped", why: "unknown", id: null, error: errorOf(b, status), mayExist: true };
	return { kind: "stopped", why: "refused", id: null, error: errorOf(b, status), mayExist: false };
}

/**
 * The upload's answer (`POST /api/v1/media`): a 201 whose `mediaKey` is a string is
 * done — the key is what the tweet is made with — and carries X's `processing.state`
 * so the run knows whether to ask again. Anything else stops the run as a refusal
 * with `mayExist: false`: an upload that failed made nothing that costs, and the
 * contract says it may simply be sent again — but the run, by then, holds a campaign
 * and a line item, so Create stays off and the way on is the panels.
 */
export function mediaStep(status: number, body: unknown): StepState {
	const b = record(body);
	const key = typeof b.mediaKey === "string" && b.mediaKey !== "" ? b.mediaKey : null;
	if (status === 201 && key !== null) {
		return { kind: "done", id: key, processing: processingState(b.processing) };
	}
	return { kind: "stopped", why: "refused", id: null, error: errorOf(b, status), mayExist: false };
}

/** `processing.state` out of a media answer, or null when X sent no processing_info. */
function processingState(processing: unknown): string | null {
	const p = record(processing);
	return typeof p.state === "string" ? p.state : null;
}

/**
 * Whether a done upload may go into the tweet, or must be asked about again, or has
 * failed — X's own three readings of `processing_info`: absent (ready), «pending» /
 * «in_progress» (ask again after `checkAfterSecs`), «succeeded» (ready), «failed».
 */
export type MediaReadiness = "ready" | "wait" | "failed";

export function mediaReadiness(processing: string | null | undefined): MediaReadiness {
	if (processing === null || processing === undefined || processing === "succeeded") return "ready";
	if (processing === "pending" || processing === "in_progress") return "wait";
	return "failed";
}

/** How many times the run asks X about one upload before it stops rather than waits on. */
export const MEDIA_STATUS_ASKS_MAX = 10;

/**
 * The card's answer (`POST /api/v1/cards`), through the contract's words: a 201 with an
 * `id` and a `cardUri` is done; `created: true` is a card that exists and may not be made
 * again; `created: "unknown"` may exist; the rest — a refusal, or the stop before the card
 * (`step: "media_library"`) — made no card.
 */
export function cardStep(status: number, body: unknown): StepState {
	const b = record(body);
	const id = typeof b.id === "string" && b.id !== "" ? b.id : null;
	const cardUri = typeof b.cardUri === "string" && b.cardUri !== "" ? b.cardUri : null;
	if (b.created === true)
		return { kind: "stopped", why: "exists", id, error: errorOf(b, status), mayExist: true };
	if (b.created === "unknown")
		return { kind: "stopped", why: "unknown", id: null, error: errorOf(b, status), mayExist: true };
	if (status === 201 && id !== null && cardUri !== null) return { kind: "done", id, cardUri };
	if (status === 201)
		return { kind: "stopped", why: "unknown", id, error: errorOf(b, status), mayExist: true };
	return { kind: "stopped", why: "refused", id: null, error: errorOf(b, status), mayExist: false };
}

/**
 * The status read's answer (`GET /api/v1/media/:id`), folded into the done step it is
 * about: the key stays, the processing state is X's latest. A read that is not a 200
 * stops the run — nothing more is sent on a media X will not report on.
 */
export function mediaStatusStep(prev: StepState, status: number, body: unknown): StepState {
	if (prev.kind !== "done") return prev;
	const b = record(body);
	if (status !== 200) {
		return {
			kind: "stopped",
			why: "refused",
			id: prev.id,
			error: errorOf(b, status),
			mayExist: false,
		};
	}
	return { ...prev, processing: processingState(b.processing) };
}

/** The upload's numeric id — what the status read is asked with — from the same answer. */
export function mediaUploadId(body: unknown): string | null {
	const b = record(body);
	return typeof b.id === "string" && b.id !== "" ? b.id : null;
}

/**
 * The tweet's answer (`POST /api/v1/tweets`), through the contract's four words: a 201
 * with an `id` is done; `created: true` is a tweet that exists and may not be made
 * again (its `id` when the answer carried one); `created: "unknown"` may exist; the
 * rest is a refusal that made nothing.
 */
export function tweetStep(status: number, body: unknown): StepState {
	const b = record(body);
	const id = typeof b.id === "string" && b.id !== "" ? b.id : null;
	if (b.created === true)
		return { kind: "stopped", why: "exists", id, error: errorOf(b, status), mayExist: true };
	if (b.created === "unknown")
		return { kind: "stopped", why: "unknown", id: null, error: errorOf(b, status), mayExist: true };
	if (status === 201 && id !== null) return { kind: "done", id };
	if (status === 201)
		return { kind: "stopped", why: "unknown", id: null, error: errorOf(b, status), mayExist: true };
	return { kind: "stopped", why: "refused", id: null, error: errorOf(b, status), mayExist: false };
}

/**
 * The request threw instead of answering — the browser's fetch failed, or the answer
 * was not JSON. It may have reached X, so this is `unknown` and never a refusal, the
 * reading `createThrew` gives in campaigns.ts.
 */
export function threwStep(message: string): StepState {
	return { kind: "stopped", why: "unknown", id: null, error: message, mayExist: true };
}

/** Whether every step answered `done` — the Start block opens on this and nothing else. */
/** Every step of a run, in the order they are sent. */
function steps(run: Run): StepState[] {
	return [
		run.campaign,
		run.lineItem,
		...run.targeting.map((t) => t.state),
		...run.media.map((m) => m.state),
		...(run.card === null ? [] : [run.card]),
		...(run.tweet === null ? [] : [run.tweet]),
		run.ad,
	];
}

export function runDone(run: Run): boolean {
	return steps(run).every((s) => s.kind === "done");
}

/** Whether some step stopped the run. */
export function runStopped(run: Run): boolean {
	return steps(run).some((s) => s.kind === "stopped");
}

/**
 * What is in X after a stopped run, for the sentence that says not to retry. 🔴 The
 * tweet is named: a nullcast tweet made and not promoted stays in the account, and a
 * person who cannot see it would make it again. An upload is not: a media key that was
 * never put on a tweet expires on its own (the contract), and names nothing a person
 * has to find.
 */
export function createdSoFar(run: Run): { what: string; id: string }[] {
	const out: { what: string; id: string }[] = [];
	if (run.campaign.kind === "done") out.push({ what: "campaign", id: run.campaign.id });
	if (run.lineItem.kind === "done") out.push({ what: "line item", id: run.lineItem.id });
	for (const t of run.targeting)
		if (t.state.kind === "done") out.push({ what: `targeting ${t.name}`, id: t.state.id });
	// 🔴 A card made and not tweeted stays in the account as the tweet does.
	if (run.card !== null && run.card.kind === "done") out.push({ what: "card", id: run.card.id });
	if (run.tweet !== null && run.tweet.kind === "done")
		out.push({ what: "tweet", id: run.tweet.id });
	if (run.ad.kind === "done") out.push({ what: "promoted tweet", id: run.ad.id });
	return out;
}

/** Whether some stopped step may have made an object the answer did not name. */
export function somethingMayExist(run: Run): boolean {
	return steps(run).some((s) => s.kind === "stopped" && s.mayExist);
}

/** The id the promote sends: the tweet this run made, or the existing one chosen. */
export function tweetIdToPromote(run: Run, chosen: Pick<Tweet, "id"> | null): string | null {
	if (run.tweet !== null) return run.tweet.kind === "done" ? run.tweet.id : null;
	return chosen?.id ?? null;
}

/** The media keys the tweet is made with, in file order — every upload done and ready. */
export function mediaKeysMade(run: Run): string[] | null {
	const keys: string[] = [];
	for (const m of run.media) {
		if (m.state.kind !== "done" || mediaReadiness(m.state.processing) !== "ready") return null;
		keys.push(m.state.id);
	}
	return keys;
}

/**
 * 🔴 Whether Create (paused) may be pressed again after this run: only when nothing
 * was made and nothing may have been — a refusal at the campaign step. After anything
 * else the button stays off for this draft, because every object it would make again
 * already exists or may; the way on is the Campaigns screen, by the ids shown.
 */
export function mayCreateAgain(run: Run | null): boolean {
	if (run === null) return true;
	return createdSoFar(run).length === 0 && !somethingMayExist(run) && runStopped(run);
}

/** The criteria the run made, for the start gate: an empty list is «untargeted». */
export function criteriaMade(run: Run): { id: string; operatorType?: "NE" }[] {
	// With its operator: an excluded country made alone is no narrowing (startGate, line-items.ts).
	return run.targeting.flatMap((t) =>
		t.state.kind === "done"
			? [{ id: t.state.id, ...(t.operatorType === "NE" ? { operatorType: t.operatorType } : {}) }]
			: [],
	);
}

/**
 * The number Ads Manager shows for a campaign — see the header: observed twice, equal
 * to the API id read as base36 both times. `null` for an id that is not base36, so
 * nothing prints `NaN` beside a campaign.
 */
export function adsManagerNumber(id: string): string | null {
	if (!/^[0-9a-z]+$/.test(id)) return null;
	return String(Number.parseInt(id, 36));
}
